import { Directive, HostBinding, HostListener, Input, inject } from '@angular/core';
import { Router } from '@angular/router';

/**
 * Link cuyo destino viene de Strapi (campo "Link del botón").
 *
 * Reemplaza a `[routerLink]="urlDeStrapi"`, que solo sirve para rutas internas
 * sin parámetros. Con routerLink:
 *   - "https://andesmotor.cl"      → href "/https:/andesmotor.cl" (roto)
 *   - "/cotizar?origen=home"       → href "/cotizar%3Forigen%3Dhome" (roto)
 *   - "/postventa#talleres"        → href "/postventa%23talleres" (roto)
 *
 * Esta directiva acepta lo que el administrador escriba:
 *   - ruta interna (/algo, /algo?x=1, /algo#ancla) → navegación de Angular
 *     (sin recargar la página), con query params y ancla.
 *   - ancla de la misma página (#algo)               → scroll nativo.
 *   - externa (https://, http://, //)                 → pestaña nueva.
 *   - mailto:, tel:, whatsapp (wa.me)                 → navegador.
 *   - vacío / null                                    → sin href. El template
 *     igual debe ocultar el botón con @if cuando no hay link.
 */
@Directive({
  selector: 'a[appCmsLink]',
  standalone: true,
})
export class CmsLinkDirective {
  private readonly router = inject(Router);

  private url = '';

  @Input({ required: true })
  set appCmsLink(value: string | null | undefined) {
    this.url = (value ?? '').trim();
  }

  /** Forzar pestaña: "_blank" | "_self". Si no viene, se decide por la URL. */
  @Input() cmsTarget: string | null | undefined;

  @HostBinding('attr.href')
  get href(): string | null {
    return this.url || null;
  }

  @HostBinding('attr.target')
  get target(): string | null {
    if (this.cmsTarget === '_blank' || this.cmsTarget === '_self') return this.cmsTarget;
    return isExternalUrl(this.url) ? '_blank' : null;
  }

  @HostBinding('attr.rel')
  get rel(): string | null {
    return this.target === '_blank' ? 'noopener noreferrer' : null;
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): boolean {
    if (!this.url) {
      event.preventDefault();
      return false;
    }
    // Ctrl/Cmd/Shift/rueda: dejar que el navegador abra pestaña nueva.
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return true;
    }
    if (!isInternalRoute(this.url) || this.target === '_blank') {
      return true;
    }
    event.preventDefault();
    const fragment = this.url.includes('#') ? this.url.split('#')[1] : '';
    this.router.navigateByUrl(this.url).then(() => {
      if (fragment) scrollToFragmentWhenReady(fragment);
    });
    return false;
  }
}

/**
 * Las secciones de destino se pintan cuando llega el contenido de Strapi, así
 * que el scroll de Angular al ancla (#talleres) llega antes de que exista el
 * elemento y la página queda arriba. Se reintenta hasta que aparece (máx. 4 s).
 */
export function scrollToFragmentWhenReady(fragment: string, timeoutMs = 4000): void {
  if (typeof document === 'undefined' || !fragment) return;
  const start = Date.now();
  const tick = () => {
    const el = document.getElementById(fragment);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (Date.now() - start < timeoutMs) setTimeout(tick, 100);
  };
  tick();
}

export function isExternalUrl(url: string | null | undefined): boolean {
  return !!url && /^(https?:)?\/\//i.test(url.trim());
}

/** Ruta del propio sitio ("/algo..."), no "//host" (externa). */
export function isInternalRoute(url: string | null | undefined): boolean {
  const u = (url ?? '').trim();
  return u.startsWith('/') && !u.startsWith('//');
}
